import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react'

export type ThemePreference = 'light' | 'dark' | 'system'
type EffectiveTheme = 'light' | 'dark'

interface ThemeContextValue {
  preference: ThemePreference
  effectiveTheme: EffectiveTheme
  setPreference: (value: ThemePreference) => void
}

const STORAGE_KEY = 'KegstarCollectionsApp.ThemePreference'

const ThemeContext = createContext<ThemeContextValue | null>(null)

function getSystemTheme(): EffectiveTheme {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function resolveTheme(preference: ThemePreference): EffectiveTheme {
  if (preference === 'system') return getSystemTheme()
  return preference
}

export function ThemeProvider({children}: {children: React.ReactNode}) {
  const [preference, setPreference] = useState<ThemePreference>(() => {
    if (typeof window === 'undefined') return 'system'
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw
    return 'system'
  })
  const [effectiveTheme, setEffectiveTheme] = useState<EffectiveTheme>(() =>
    resolveTheme(preference)
  )

  useEffect(() => {
    setEffectiveTheme(resolveTheme(preference))
    window.localStorage.setItem(STORAGE_KEY, preference)
  }, [preference])

  useEffect(() => {
    if (preference !== 'system') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleThemeChange = () => {
      setEffectiveTheme(mediaQuery.matches ? 'dark' : 'light')
    }

    handleThemeChange()
    mediaQuery.addEventListener('change', handleThemeChange)
    return () => mediaQuery.removeEventListener('change', handleThemeChange)
  }, [preference])

  useEffect(() => {
    const root = document.documentElement
    root.classList.toggle('dark', effectiveTheme === 'dark')
    root.style.colorScheme = effectiveTheme
  }, [effectiveTheme])

  const setPreferenceSafe = useCallback((value: ThemePreference) => {
    setPreference(value)
  }, [])

  const value = useMemo(
    () => ({
      preference,
      effectiveTheme,
      setPreference: setPreferenceSafe,
    }),
    [preference, effectiveTheme, setPreferenceSafe]
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) throw new Error('useTheme must be used within a ThemeProvider')
  return context
}
